import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ProductBrand } from '@/components/brand/ProductBrand';
import { Card, CardHeader } from '@/components/ui/Card';
import { LoadingScreen, Spinner } from '@/components/ui/Spinner';
import { needsOnboarding } from '@/api/onboarding';
import { getStoredSession } from '@/api/authStorage';

export function OAuthCallbackPage() {
  const { completeOAuthLogin, isAuthenticated, loading, user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  const code = searchParams.get('code');
  const googleError = searchParams.get('google');
  const message = searchParams.get('message');

  useEffect(() => {
    if (loading || started.current) return;

    if (googleError === 'error' || (!code && message)) {
      setError(message || 'Google sign-in failed');
      return;
    }

    if (!code) {
      setError('Missing authorization code');
      return;
    }

    started.current = true;
    void (async () => {
      const { error: err } = await completeOAuthLogin(code);
      if (err) {
        setError(err.message);
        return;
      }
      const nextUser = getStoredSession()?.user;
      navigate(needsOnboarding(nextUser) ? '/accept-terms' : '/dashboard', { replace: true });
    })();
  }, [loading, code, googleError, message, completeOAuthLogin, navigate]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated && !error) {
    return <Navigate to={needsOnboarding(user) ? '/accept-terms' : '/dashboard'} replace />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-page px-4 py-12">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader className="space-y-5">
            <ProductBrand variant="light" showTagline={false} />
            {error ? (
              <>
                <div>
                  <h1 className="text-headline-md font-semibold text-foreground">Sign-in failed</h1>
                  <p className="mt-2 text-body-sm text-foreground-muted leading-relaxed">{error}</p>
                </div>
                <p className="text-center text-body-sm text-foreground-muted">
                  <Link to="/login" className="font-semibold text-brand-deep hover:text-brand hover:underline">
                    Back to sign in
                  </Link>
                </p>
              </>
            ) : (
              <div className="flex flex-col items-center gap-3 py-4">
                <Spinner className="w-6 h-6" />
                <p className="text-body-sm text-foreground-muted">Completing Google sign-in…</p>
              </div>
            )}
          </CardHeader>
        </Card>
      </div>
    </div>
  );
}
